import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAdminSession } from "@/hooks/useAdminSession";
import { Shield, Lock, Settings } from "lucide-react";

export default function AdminVerify() {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { verify, isValid, needsPasswordSetup } = useAdminSession();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { from?: { pathname?: string } } | null;

  // Redirect if already valid
  if (isValid) {
    const from = state?.from?.pathname || "/admin";
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const success = await verify(password);
    setLoading(false);

    if (success) {
      const from = state?.from?.pathname || "/admin";
      navigate(from, { replace: true });
    }
  };

  const handleGoToSettings = () => {
    // Temporarily bypass verification for settings page
    sessionStorage.setItem('vesper_admin_setup_mode', 'true');
    navigate("/admin/settings", { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F5F0E8] p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border-2 border-black bg-white shadow-[8px_8px_0_#000]">
        <div className="bg-[#FFD93D] p-8 border-b-2 border-black flex flex-col items-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-black bg-white shadow-[4px_4px_0_#000]">
            <Shield className="h-8 w-8 text-black" />
          </div>
          <h1 className="text-2xl font-black text-black">{t('admin.verify.title')}</h1>
          <p className="mt-2 font-bold text-black/70">{t('admin.verify.subtitle')}</p>
        </div>

        {needsPasswordSetup ? (
          <div className="p-8">
            <div className="mb-6 rounded-xl border-2 border-black bg-[#FFE066] p-4">
              <p className="font-bold text-black">{t('admin.verify.setupRequired')}</p>
              <p className="mt-1 text-sm text-black/70">{t('admin.verify.setupDescription')}</p>
            </div>
            <button
              onClick={handleGoToSettings}
              className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-black bg-[#6FEDD6] py-3 font-black text-black shadow-[4px_4px_0_#000] transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_#000] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
            >
              <Settings className="h-5 w-5" />
              {t('admin.verify.goToSettings')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-8">
            <div className="mb-6">
              <label className="mb-2 block font-bold text-gray-700">{t('admin.verify.password')}</label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-xl border-2 border-black bg-gray-50 py-3 pl-10 pr-4 font-bold outline-none focus:bg-white focus:ring-2 focus:ring-[#FFD93D]"
                  placeholder={t('admin.verify.placeholder')}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl border-2 border-black bg-[#6FEDD6] py-3 font-black text-black shadow-[4px_4px_0_#000] transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_#000] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('admin.verify.verifying') : t('admin.verify.confirm')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
